import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  Button,
  Col,
  Input,
  Row,
  Select,
  Switch,
  Space,
  Tag,
  Modal,
  DatePicker,
  TimePicker,
  Tooltip,
  Drawer,
  Form,
  Carousel,
} from "antd";
import ViewTableBtn from "../../assets/images/view-icon.svg";
import EditTableBtn from "../../assets/images/action-edit-btn.svg";
import DeleteTableBtn from "../../assets/images/delete-bin-icon.svg";
import DeleteModalBtn from "../../assets/images/delete-icon-modal.svg";
import HeaderPostIcon from "../../assets/images/header-post-icon.svg";
import { Severty, ShowToast } from "../../helper/toast";
import { useNavigate, useParams } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import moment from "moment";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  CopyOutlined,
  RobotOutlined,
  CheckOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import MultipleImageUpload from "../../components/MultipleImageUpload"; // Make sure this component exists
import NormalEditor from "../../components/NormalEditor";

const { TextArea } = Input;

const getBrowserTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

const getTimezoneOffsetForSchedule = (dateValue, timeValue) => {
  if (!dateValue || !timeValue) {
    return new Date().getTimezoneOffset();
  }

  const [year, month, day] = String(dateValue).split("-").map(Number);
  const [hours, minutes] = String(timeValue).split(":").map(Number);

  if (
    [year, month, day, hours, minutes].some(
      (part) => Number.isNaN(part) || part == null,
    )
  ) {
    return new Date().getTimezoneOffset();
  }

  return new Date(year, month - 1, day, hours, minutes, 0, 0).getTimezoneOffset();
};

const getScheduledMoment = (post) => {
  if (!post?.selectedDate) return null;

  const scheduledDate = moment.utc(post.selectedDate).format("YYYY-MM-DD");

  // No time chosen yet — the common case for a fresh Draft. Running a fake
  // "00:00" default through the timezoneOffset conversion (meant for a
  // REAL wall-clock time) produced a UTC instant that could land on the
  // PREVIOUS calendar day once rendered in a viewer's local timezone. A
  // date with no time attached has no timezone-dependent instant to
  // compute — treat it as a pure calendar day, UTC-anchored, identical for
  // every viewer. See FullCalender.js's getScheduledMoment for the full
  // writeup of this bug.
  if (!post.selectedTime) {
    return moment.utc(`${scheduledDate}T00:00:00`);
  }

  const timezoneOffset = Number.isFinite(Number(post?.timezoneOffset))
    ? Number(post.timezoneOffset)
    : new Date().getTimezoneOffset();
  const [year, month, day] = scheduledDate.split("-").map(Number);
  const [hours, minutes] = String(post.selectedTime).split(":").map(Number);

  return moment(
    Date.UTC(year, month - 1, day, hours, minutes, 0, 0) +
      timezoneOffset * 60 * 1000,
  );
};

const isPostScheduledForFuture = (post) => {
  const scheduledAt = getScheduledMoment(post);
  return scheduledAt?.isValid() && scheduledAt.isAfter(moment());
};

const stripHtml = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html || "";
  return div.textContent || div.innerText || "";
};

const isPostPastDue = (post) => {
  const scheduledAt = getScheduledMoment(post);
  return scheduledAt?.isValid() && scheduledAt.isSameOrBefore(moment());
};

const getPostStatusText = (post) =>
  [post?.brandStatus, post?.clientStatus]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const hasPostReviewStatus = (post) => getPostStatusText(post).includes("review");

// Includes "linkedin token expired" too — the backend's own distinct status
// for a post that missed its window specifically because the client's
// LinkedIn token was expired at the time (see getPostDisplayStatus below for
// its own clearer label). Both belong to the same "delayed" family for
// bucketing purposes (dashboard filter, excluded from isPostPublished).
const hasPostDelayedStatus = (post) => {
  const t = getPostStatusText(post);
  return t.includes("delay") || t.includes("linkedin token expired");
};

const isPostPublished = (post) =>
  Boolean(post?.postId) && !hasPostReviewStatus(post) && !hasPostDelayedStatus(post);

const getPrimaryStatus = (post) => {
  const statuses = [post?.brandStatus, post?.clientStatus].filter(Boolean);
  return statuses.find((status) => String(status).toLowerCase().includes("review")) || statuses[0];
};

const isPostDelayed = (post) => {
  if (isPostPublished(post)) return false;

  const normalizedStatus = getPostStatusText(post);
  if (hasPostDelayedStatus(post)) return true;

  return (
    isPostPastDue(post) &&
    !isPostPublished(post) &&
    normalizedStatus.includes("publish")
  );
};

// In-Review = the whole content-review pipeline, start to finish: internal
// review pending → internal review approved → sent to client for review.
// The post only leaves once the client approves it (moving it to Pending)
// or it's published. Keep in sync with ACTIVE_REVIEW_STATUSES in
// Admin/AccessModuleController.ts.
const ACTIVE_REVIEW_STATUSES = ["review", "under review", "sent for review", "internal review pending", "internal review approved", "sent to client", "changes requested", "changes requested by client"];
// Mirrors the Calendar view's ROLE_DESIGNATIONS (FullCalender.js) — the
// role a Client's Assistant acted under is "assistant", not "user"/"Client",
// so the label doesn't misattribute an Assistant's action as the Client's own.
const ROLE_DESIGNATIONS = { user: "Client", admin: "Admin", brand: "Brand", assistant: "Assistant" };
const hasActiveReviewStatus = (post) => {
  const statuses = [post?.brandStatus, post?.clientStatus]
    .filter(Boolean)
    .map((s) => String(s).toLowerCase());
  return ACTIVE_REVIEW_STATUSES.some((status) => statuses.includes(status));
};

// Pending = the client has already approved it (clientApprovedByName is
// only ever set by the client's own approve action) but it hasn't been
// flipped to action="publish" yet — nothing left except that final trigger.
// Excludes anything already flagged "delayed" (status text) — those belong
// in the Delayed bucket, not Pending.
const isPostApprovedAwaitingPublish = (post) =>
  Boolean(post?.clientApprovedByName) && post?.action !== "publish" && !post?.postId && !hasPostDelayedStatus(post);

// Upcoming = locked in to auto-publish (action="publish") for a genuinely
// future moment, and not yet actually posted. A future-dated post still
// sitting in "draft"/"approved" isn't scheduled yet — it still needs to
// clear approval before anything will fire on that date.
const isPostUpcoming = (post) =>
  post?.action === "publish" && !post?.postId && isPostScheduledForFuture(post);

const matchesDashboardView = (post, dashboardView) => {
  if (!dashboardView || dashboardView === "all") return true;
  if (dashboardView === "published") return isPostPublished(post);
  if (dashboardView === "delayed") return isPostDelayed(post);
  if (dashboardView === "in-review") return !isPostDelayed(post) && hasActiveReviewStatus(post);
  if (dashboardView === "pending") return isPostApprovedAwaitingPublish(post);
  if (dashboardView === "upcoming") return isPostUpcoming(post);
  return true;
};

const formatStatusLabel = (status) => {
  if (!status) return "Draft";

  const normalizedStatus = String(status).toLowerCase();
  if (normalizedStatus === "publish") return "Published";
  if (normalizedStatus === "publish by client") return "Published by Client";

  return String(status)
    .split(" ")
    .map((word, index) =>
      index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word,
    )
    .join(" ");
};

const getPublishedStatusLabel = (post) => {
  // New: show "Published by {firstName}" when publisher identity is stored
  if (post?.publishedByName) {
    const firstName = post.publishedByName.split(" ")[0];
    return `Published by ${firstName}`;
  }

  // Fallback: existing logic for posts without publishedByName (backward-compat)
  const rawStatus = getPrimaryStatus(post);
  const normalizedStatus = String(rawStatus || "").toLowerCase();

  if (normalizedStatus === "publish by client") return "Published by Client";
  if (normalizedStatus === "publish by brand") return "Published by Brand";
  if (normalizedStatus === "publish by admin" || normalizedStatus === "publish") {
    return "Published";
  }

  if (normalizedStatus === "live") {
    const createdBy = Number(post?.createdBy);
    if (createdBy === 0) return "Published by Client";
    if (createdBy === 1) return "Published by Brand";
  }

  return "Published";
};

const getPostDisplayStatus = (post, clientFirstName) => {
  const rawStatus = getPrimaryStatus(post);
  const normalizedStatus = String(rawStatus || "").toLowerCase();

  // Draft is checked first so a stale clientApprovedByName left over from
  // an earlier approve cycle (the field isn't cleared when a post is
  // pulled back to draft) can't override it below.
  if (normalizedStatus.includes("draft")) {
    return {
      label: formatStatusLabel(rawStatus),
      color: "#f8fafc",
      textColor: "#475569",
      borderColor: "#cbd5e1",
    };
  }

  // Internal review status takes priority over the scheduled-date display
  // below — same fix/precedence as the Calendar view's getCalendarStatusLabel.
  // Without this, a future-dated post still awaiting internal (BS/BH) review
  // fell straight through to isPostScheduledForFuture and misleadingly
  // showed "Scheduled" (reported bug).
  if (post?.internalReview?.status === "pending") {
    return {
      label: "Internal Review Pending",
      color: "#fff7ed",
      textColor: "#c2410c",
      borderColor: "#fed7aa",
    };
  }
  if (post?.internalReview?.status === "changes_requested") {
    return {
      label: "Changes Requested",
      color: "#fef2f2",
      textColor: "#b91c1c",
      borderColor: "#fecaca",
    };
  }

  // Post-publish edit cycle — mirrors the Calendar view's
  // getCalendarStatusLabel, including its priority order: these take
  // precedence over scheduled/delayed/published, since e.g. an
  // approved-and-scheduled post should still read as "Approved by X", not
  // "Scheduled". Checking the stored name field (rather than matching the
  // brandStatus text) also survives the "Approve By Client" vs
  // "Approved By Client" spelling used by different code paths.
  if (post?.clientApprovedByName) {
    // clientApprovedByRole distinguishes the Client's own approval from an
    // Assistant approving on their behalf — was previously unlabeled by
    // role entirely, just the first name.
    const role = ROLE_DESIGNATIONS[post?.clientApprovedByRole] || "Client";
    return {
      label: `Approved by ${role} (${post.clientApprovedByName.split(" ")[0]})`,
      color: "#f0fdf4",
      textColor: "#15803d",
      borderColor: "#bbf7d0",
    };
  }
  const isAwaitingClientReview =
    !!post?.sentToClientByName ||
    normalizedStatus === "sent to client" ||
    normalizedStatus === "sent for review" ||
    normalizedStatus === "under review";
  if (isAwaitingClientReview) {
    return {
      label: clientFirstName ? `Sent to ${clientFirstName}` : "Sent to Client",
      color: "#fff7ed",
      textColor: "#c2410c",
      borderColor: "#fed7aa",
    };
  }

  // Same fix as the Calendar view's getCalendarStatusLabel — the client's
  // own "Changes Requested" status otherwise falls through to the
  // scheduled-date check below and misleadingly shows "Scheduled".
  if (normalizedStatus.includes("changes requested")) {
    const requestedRole = ROLE_DESIGNATIONS[post?.changesRequestedByRole] || "Client";
    const requestedLabel = post?.changesRequestedByName
      ? `Changes Requested by ${requestedRole} (${post.changesRequestedByName.split(" ")[0]})`
      : (clientFirstName ? `Changes Requested by ${clientFirstName}` : "Changes Requested");
    return {
      label: requestedLabel,
      color: "#fef2f2",
      textColor: "#b91c1c",
      borderColor: "#fecaca",
    };
  }

  if (isPostScheduledForFuture(post)) {
    return {
      label: "Scheduled",
      color: "#fffbeb",
      textColor: "#b45309",
      borderColor: "#fde68a",
    };
  }

  // Checked before the generic Delayed case below, same red styling but a
  // clear, specific label instead of making an Admin open the post to find
  // out why.
  if (normalizedStatus === "linkedin token expired") {
    return {
      label: "LinkedIn Token Expired",
      color: "#fef2f2",
      textColor: "#b91c1c",
      borderColor: "#fecaca",
    };
  }

  if (isPostDelayed(post)) {
    return {
      label: "Delayed",
      color: "#fef2f2",
      textColor: "#b91c1c",
      borderColor: "#fecaca",
    };
  }

  if (isPostPublished(post)) {
    return {
      label: getPublishedStatusLabel(post),
      color: "#eff6ff",
      textColor: "#1d4ed8",
      borderColor: "#bfdbfe",
    };
  }

  if (normalizedStatus.includes("publish")) {
    return {
      label: "Publish Pending",
      color: "#fff7ed",
      textColor: "#c2410c",
      borderColor: "#fed7aa",
    };
  }

  if (normalizedStatus.includes("review")) {
    return {
      label: formatStatusLabel(rawStatus),
      color: "#fff7ed",
      textColor: "#c2410c",
      borderColor: "#fed7aa",
    };
  }

  if (normalizedStatus.includes("approve")) {
    return {
      label: formatStatusLabel(rawStatus),
      color: "#f0fdf4",
      textColor: "#15803d",
      borderColor: "#bbf7d0",
    };
  }

  return {
    label: formatStatusLabel(rawStatus),
    color: "#f8fafc",
    textColor: "#475569",
    borderColor: "#cbd5e1",
  };
};

const resolveMetricCount = (primaryValue, ...fallbackValues) => {
  const candidates = [primaryValue, ...fallbackValues];

  for (const value of candidates) {
    if (Array.isArray(value)) {
      return value.length;
    }

    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) {
      return numericValue;
    }
  }

  return 0;
};

// Permission helper
const getPermissionByName = (moduleName) => {
  try {
    const user = JSON.parse(localStorage.getItem("admin_userProfile") || "{}");
    if (user.type === "Admin") {
      return { view: true, add: true, edit: true, delete: true };
    }
    // Case/whitespace-insensitive match — see BlogTabs.js's identical fix.
    const normalizedTarget = String(moduleName || "").trim().toLowerCase();
    const permissions = user?.roleId?.permission || [];
    return (
      permissions.find(
        (p) => String(p?.name || "").trim().toLowerCase() === normalizedTarget
      ) || {}
    );
  } catch {
    return {};
  }
};

const normalizePostType = (type) => {
  if (type === "Title" || type === "Image" || type === "Video") return "Media";
  return type || "Media";
};

const ClientPostTabs = ({ dashboardView = "all", user }) => {
  const navigate = useNavigate();
  const clientFirstName = (user?.name || user?.linkedinName || "").split(" ")[0] || null;
  const postsPermission = getPermissionByName("Posts");
  
  // Redirect if user lacks view permission
  useEffect(() => {
    if (!postsPermission?.view) {
      navigate("/dashboard");
    }
  }, [postsPermission?.view]);
  const { id } = useParams();
  const [form] = Form.useForm();
  const { request } = useRequest();
  const [isDisable, setIsDisable] = useState(true);
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [rescheduleModalVisible, setRescheduleModalVisible] = useState(false);
  const [reschedulePost, setReschedulePost] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduleTime, setRescheduleTime] = useState(null);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [postId, setPostId] = useState("");
  const [selectedPostType, setSelectedPostType] = useState("Media");
  const [pollQuestion, setPollQuestion] = useState("");
  const [pollOptions, setPollOptions] = useState(["", ""]);
  const [pollResults, setPollResults] = useState(null);
  const [pollResultsLoading, setPollResultsLoading] = useState(false);
  const [pollResultsForbidden, setPollResultsForbidden] = useState(false);
  const [pendingPostType, setPendingPostType] = useState(null);
  const [typeChangeModalVisible, setTypeChangeModalVisible] = useState(false);
  const [isLinkedInPosted, setIsLinkedInPosted] = useState(false);
  const [postText, setPostText] = useState("");
  const [postAnalysis, setPostAnalysis] = useState(null);
  const [postAnalysisLoading, setPostAnalysisLoading] = useState(false);
  // Content Intelligence analysis can take up to ~110s. The Analyze button
  // is disabled while a request is in flight, but nothing stops the user
  // from continuing to edit the post text in that window — when the slow
  // response lands, it would otherwise silently overwrite postAnalysis with
  // a score for whatever text was in the box when Analyze was clicked.
  const postTextLiveRef = useRef(postText);
  useEffect(() => {
    postTextLiveRef.current = postText;
  }, [postText]);
  // Guideline Checker — our own LLM-based feature (originality.ai's version
  // has no API access at all, confirmed against their published docs).
  const [postGuidelineCheck, setPostGuidelineCheck] = useState(null);
  const [postGuidelineCheckLoading, setPostGuidelineCheckLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [themeData, setThemeData] = useState([]);
  const [subPillars, setSubPillars] = useState([]);
  const [record, setRecord] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const isScheduledLocked = isEditMode && isPostScheduledForFuture(record) && !isDirty;
  // Once a post is live on LinkedIn, only its text can still change —
  // media, schedule, tags/pillars, and post type are locked. Mirrors the
  // backend guard in AccessModuleController.editCalender.
  const isPublishedLocked = isEditMode && isPostPublished(record);
  const [attachment, setAttachment] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [actionType, setActionType] = useState("");
  const [selectedTime, setSelectedTime] = useState(null);
  const [editId, setEditId] = useState(null);
  const [lastComment, setLastComment] = useState("");
  const [isMarkExternalModalOpen, setIsMarkExternalModalOpen] = useState(false);
  const [linkedinUrlInput, setLinkedinUrlInput] = useState("");
  const [markExternalLoading, setMarkExternalLoading] = useState(false);

  const fetchData = () => {
  let url = "/admin/access-module/post/" + id;
  const query = [];
  const useDashboardFilter = dashboardView && dashboardView !== "all";
  if (!useDashboardFilter) {
    if (searchText) query.push(`name=${encodeURIComponent(searchText)}`);
    if (selectedDate)
      query.push(`date=${encodeURIComponent(selectedDate.format("YYYY-MM-DD"))}`);
    if (selectedStatus) query.push(`status=${encodeURIComponent(selectedStatus)}`);
    query.push(`page=${currentPage}`);
    query.push(`limit=${pageSize}`);
  } else {
    query.push("page=1");
    query.push("limit=1000");
  }
  if (query.length) url += "?" + query.join("&");

  request({
    url,
    method: "GET",
    onSuccess: (response) => {
      if (response) {
        const serverList = response.data.list || [];
        if (!useDashboardFilter) {
          setData(serverList);
          setTotal(response.data.pagination.totalItems || 0);
          setCurrentPage(response.data.pagination.page || 1);
          setPageSize(response.data.pagination.limit || 10);
          return;
        }
        const filteredList = serverList.filter((post) => matchesDashboardView(post, dashboardView));
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        setData(filteredList.slice(startIndex, endIndex));
        setTotal(filteredList.length);
      } else {
        ShowToast(response.message || "Failed to fetch posts", Severty.ERROR);
      }
    },
    onError: (error) => {
      console.error("Error fetching posts:", error);
      ShowToast("Error fetching post data", Severty.ERROR);
    },
  });
};

  const resetForm = () => {
    form.resetFields();
    setPostText("");
    setAttachment([]);
    setSelectedPostType("Media");
    setPollQuestion("");
    setPollOptions(["", ""]);
    setPollResults(null);
    setSubPillars([]);
  };

  const onClose = () => {
    setOpen(false);
    setEditId(null);
  };
  const contentThemeData = async () => {
      try {
        let url = "/admin/access-module/content/" + id;
  
        request({
          url,
          method: "GET",
          onSuccess: (data) => {
            if (data.status === 200) {
              setThemeData(data.data.themes);
            } else {
              ShowToast(data.message, Severty.ERROR);
            }
          },
          onError: () => {
            // ShowToast(`Error ${"creating"} blog`, Severty.ERROR);
          },
          // onFinally: () => setLoading(false),
        });
      } catch (error) {
        console.log("error", error);
      }
    };

  const handleThemeChange = (value) => {
    const selected = themeData.find((theme) => theme._id === value);
    setSelectedTheme(selected);
    setSubPillars(selected ? selected.subPillars : []);
  };

  const handleStatusClick = (status, record) => {
    setSelectedStatus(status);
    setIsModalOpen(true);
    setPostId(record);
  };

  // Apply a post-type switch: update state and clear type-specific fields
  const applyPostTypeChange = (newType) => {
    setSelectedPostType(newType);
    setAttachment([]);
    setPollQuestion("");
    setPollOptions(["", ""]);
    setPollResults(null);
    form.setFieldsValue({
      mediaType: newType,
      carouselDocumentTitle: undefined,
      pollQuestion: undefined,
      pollDuration: "3",
    });
  };

  const handleAnalyzePostContent = async () => {
    const textToAnalyze = stripHtml(postText || "").trim();
    if (!textToAnalyze) {
      ShowToast("Write some content first.", Severty.ERROR);
      return;
    }
    setPostAnalysisLoading(true);
    handleCheckPostGuidelines(textToAnalyze); // fire alongside analysis, not awaited — own loading state
    try {
      const res = await request({
        url: "/admin/access-module/research/analyze",
        method: "POST",
        data: { text: textToAnalyze, language: "en" },
      });
      if (!res?.data) throw new Error("No analysis returned");
      // Discard this result if the editor's content changed while the
      // request was in flight.
      if (stripHtml(postTextLiveRef.current || "").trim() !== textToAnalyze) {
        return;
      }
      setPostAnalysis(res.data);
      ShowToast("Content analyzed!", Severty.SUCCESS);
    } catch {
      ShowToast("Analysis failed.", Severty.ERROR);
    } finally {
      setPostAnalysisLoading(false);
    }
  };

  const handleCheckPostGuidelines = async (text) => {
    const textToCheck = text || stripHtml(postText || "").trim();
    if (!textToCheck) {
      ShowToast("Write some content first.", Severty.ERROR);
      return;
    }
    setPostGuidelineCheckLoading(true);
    try {
      const res = await request({
        url: "/admin/access-module/research/guideline-check",
        method: "POST",
        data: { text: textToCheck, guideline: "AP-inspired" },
      });
      if (!res?.data) throw new Error("No guideline check result returned");
      setPostGuidelineCheck(res.data);
    } catch {
      ShowToast("Guideline check failed.", Severty.ERROR);
    } finally {
      setPostGuidelineCheckLoading(false);
    }
  };

  const handleCopyPostGuidelineSuggestion = async (suggestion) => {
    try {
      await navigator.clipboard.writeText(suggestion);
      ShowToast("Suggestion copied!", Severty.SUCCESS);
    } catch {
      ShowToast("Could not copy — clipboard access blocked.", Severty.ERROR);
    }
  };

  const handlePostTypeChange = (value) => {
    const strippedText = (postText || "").replace(/<[^>]*>/g, "").trim();
    const hasData =
      strippedText.length > 0 ||
      (attachment && attachment.length > 0) ||
      pollQuestion.trim().length > 0 ||
      pollOptions.some((opt) => opt.trim().length > 0) ||
      !!form.getFieldValue("carouselDocumentTitle");

    if (hasData) {
      form.setFieldsValue({ mediaType: selectedPostType });
      setPendingPostType(value);
      setTypeChangeModalVisible(true);
    } else {
      applyPostTypeChange(value);
    }
  };

  const handleAction = (action) => {
    request({
      url: `/admin/access-module/post-status/${postId}`,
      method: "PUT",
      data: { status: action, comment },
      onSuccess: () => {
        fetchData();
      },
    });
    setIsModalOpen(false);
  };

  const handleMarkPublishedExternally = () => {
    const url = linkedinUrlInput.trim();
    if (!url) {
      ShowToast("Please enter the LinkedIn post URL", Severty.ERROR);
      return;
    }
    if (!/^https?:\/\/.+/i.test(url)) {
      ShowToast("Please enter a valid URL", Severty.ERROR);
      return;
    }
    setMarkExternalLoading(true);
    request({
      url: `/admin/access-module/post/${postId}/mark-published-externally`,
      method: "PUT",
      data: { linkedinUrl: url },
      onSuccess: (data) => {
        setMarkExternalLoading(false);
        ShowToast(data?.message || "Post marked as published on LinkedIn", Severty.SUCCESS);
        setIsMarkExternalModalOpen(false);
        setLinkedinUrlInput("");
        fetchData();
      },
      onError: (data) => {
        setMarkExternalLoading(false);
        ShowToast(data?.message || "Failed to mark as published", Severty.ERROR);
      },
    });
  };

  const onFinish = async (values) => {
    try {
      const urls = attachment && attachment.length ? attachment.map((item) => item.url) : "";
      const isVideoAttachment = (url) => /\.(mp4|mov|avi|webm|mkv|m4v)(\?|$)/i.test(url || "");
      let attachmentType = "none";
      if (values.mediaType === "Carousel") {
        attachmentType = "carousel";
      } else if (values.mediaType === "Media" && urls && urls.length > 0) {
        const firstUrl = Array.isArray(urls) ? urls[0] : String(urls).split(",")[0];
        attachmentType = isVideoAttachment(firstUrl) ? "video" : "image";
      }
      const payload = {
        ...values,
        media: urls,
        postType: values.mediaType,
        attachmentType,
        ...(values.mediaType === "Poll" ? {
          pollData: {
            question: pollQuestion,
            options: pollOptions.filter(Boolean),
            duration: form.getFieldValue("pollDuration") || "3",
          },
        } : {}),
        selectedDate,
        selectedTime,
        timezone: getBrowserTimezone(),
        timezoneOffset: getTimezoneOffsetForSchedule(selectedDate, selectedTime),
        comment: values.commentbox,
        action: actionType,
        clientStatus:
          actionType === "draft"
            ? "Draft By Admin"
            : actionType === "review"
            ? "Under Review"
            : actionType === "publish"
            ? "Publish By Admin"
            : actionType,
        brandStatus:
          actionType === "draft"
            ? "Draft"
            : actionType === "review"
            ? "Sent For Review"
            : actionType === "publish"
            ? "Publish"
            : actionType,
      };
      
      request({
        url: "/admin/access-module/calender/" + record?._id || "",
        method: "PUT",
        data: payload,
        onSuccess: (data) => {
          if (data.status === 200) {
            ShowToast(
              actionType === "draft"
                ? "Post Draft"
                : actionType === "review"
                ? "Post sent For Review"
                : `${actionType} successfully`,
              Severty.SUCCESS
            );
            setSelectedDate(null);
            setSelectedTime(null);
            form.resetFields();
            resetForm();
            fetchData();
          } else {
            ShowToast(data.message, Severty.ERROR);
          }
        },
        onError: () => {
          ShowToast(`Error ${actionType === "draft" ? "saving draft" : actionType === "review" ? "sending for review" : "processing"} post`, Severty.ERROR);
        },
      });
    } catch (error) {
      console.log("Error adding data", error);
      ShowToast("Error adding data.", Severty.ERROR);
    } finally {
      setOpen(false);
      fetchData();
    }
  };

  const deleteContent = () => {
    request({
      url: `/admin/access-module/calender/${selected?._id}`,
      method: "DELETE",
      onSuccess: (data) => {
        if (data.status === 200) {
          ShowToast(data.message, Severty.SUCCESS);
          setDeleteModalVisible(false);
          fetchData();
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: (data) => {
        ShowToast(data?.message || "Something went wrong", Severty.ERROR);
      },
    });
  };

 const getCommentStyle = (type) => ({
    background: type === "Brand" ? "#e6f7ff" : "#fff1f0",
    border: `1px solid ${type === "brand" ? "#91d5ff" : "#ffa39e"}`,
    color: "#333",
    padding: "10px 12px",
    borderRadius: "12px",
    maxWidth: "70%",
    wordBreak: "break-word",
  });

  const columns = [
    {
      title: "TITLE",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "STATUS",
      dataIndex: "brandStatus",
      key: "brandStatus",
      render: (_, row) => {
        const displayStatus = getPostDisplayStatus(row, clientFirstName);

        return (
          <Tag
            style={{
              backgroundColor: displayStatus.color,
              color: displayStatus.textColor,
              borderColor: displayStatus.borderColor,
              borderRadius: "50px",
              padding: "6px 12px",
              fontWeight: "700",
              cursor: "pointer",
              borderStyle: "solid",
              textTransform: "uppercase",
              margin: 0,
            }}
          >
            {displayStatus.label}
          </Tag>
        );
      },
    },
    {
      title: "LAST UPDATE",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (value) =>
        value ? moment(value).format("YYYY-MM-DD HH:mm") : "--",
    },
    {
      title: "PUBLISH DATE",
      dataIndex: "selectedDate",
      key: "selectedDate",
      render: (_, row) => {
        const scheduledAt = getScheduledMoment(row);
        return scheduledAt?.isValid()
          ? scheduledAt.format("YYYY-MM-DD hh:mm A")
          : "--";
      },
    },
    {
      title: "CONTENT THEME",
      dataIndex: "contentTheme",
      key: "contentTheme",
      render: (contentTheme) => <span>{contentTheme?.name || "--"}</span>,
    },
    {
      title: "SUB-PILLAR",
      dataIndex: "subPillar",
      key: "subPillar",
      render: (val) => <span>{val || "--"}</span>,
    },
    {
      title: "LIKES",
      dataIndex: "likes",
      key: "likes",
      render: (_, row) => (
        <span>{resolveMetricCount(row?.numLikes, row?.likes)}</span>
      ),
    },
    {
      title: "COMMENT",
      dataIndex: "commentCount",
      key: "commentCount",
      render: (_, row) => (
        <span>
          {resolveMetricCount(
            row?.numComments,
            row?.commentCount,
            row?.comments,
            row?.commentData,
          )}
        </span>
      ),
    },
    {
      title: "REPOST",
      dataIndex: "repostCount",
      key: "repostCount",
      render: (_, row) => (
        <span>
          {resolveMetricCount(
            row?.numShares,
            row?.repostCount,
            row?.shares,
            row?.reposts,
          )}
        </span>
      ),
    },
    {
      title: "ACTIONS",
      key: "actions",
      render: (_, record) => (
        <div className="action-icons">
          <Tooltip title="view">
            <Button
              className="table-light-fill-btn"
              onClick={() => {
                  // Previously opened a plain read-only "Post Details" modal
                  // here — jump straight to the real post on the Calendar
                  // tab instead, same deep-link pattern already used by the
                  // notification bell (navigate to the Calendar tab with
                  // openItem, which FullCalender.js's location.state effect
                  // consumes to open the post directly via handleEditClick).
                  navigate(`/view-clients/${id}`, {
                    state: { activeTab: "1", openItem: record },
                  });
              }}
            >
              <img src={ViewTableBtn} alt="View" />
            </Button>
          </Tooltip>
          {postsPermission?.edit && (
            <Tooltip title="Edit">
              <Button 
                className="table-primary-fill-btn" 
                onClick={async () => {
                    setOpen(true);
                    setRecord(record);
                    resetForm();
                    setIsDirty(false);
                    setAttachment(record.media || []);
                    setSelectedPostType(normalizePostType(record.postType));
                    setSubPillars(record.subPillar || []);
                    setIsEditMode(true);

                    // Populate poll fields
                    const pq = record.pollData?.question || "";
                    const po = Array.isArray(record.pollData?.options) && record.pollData.options.length >= 2
                      ? record.pollData.options : ["", ""];
                    setPollQuestion(pq);
                    setPollOptions(po);

                    form.setFieldsValue({
                      ...record,
                      mediaType: normalizePostType(record.postType),
                      contentTheme: record.contentTheme?._id,
                      pollQuestion: pq,
                      pollDuration: record.pollData?.duration || "3",
                      carouselDocumentTitle: record.carouselDocumentTitle || "",
                    });

                    // Fetch poll results if this is a Live poll
                    if (record.postType === "Poll" && record.action === "Live" && record.postId) {
                      setPollResultsLoading(true);
                      setPollResultsForbidden(false);
                      try {
                        const res = await request({
                          url: `/admin/access-module/calender/poll-results/${record._id}`,
                          method: "GET",
                        });
                        if (res?.data?.code === "FORBIDDEN" || res?.code === "FORBIDDEN") {
                          setPollResults(null);
                          setPollResultsForbidden(true);
                        } else if (res?.status === 200 && res.data?.pollResults?.options?.length) {
                          setPollResults(res.data.pollResults);
                          setPollResultsForbidden(false);
                        } else {
                          setPollResults(null);
                          setPollResultsForbidden(false);
                        }
                      } catch (e) {
                        setPollResults(null);
                        setPollResultsForbidden(false);
                      } finally {
                        setPollResultsLoading(false);
                      }
                    } else {
                      setPollResults(null);
                      setPollResultsForbidden(false);
                    }
                }}
              >
                <img src={EditTableBtn} alt="Edit" />
              </Button>
            </Tooltip>
          )}
          {isPostScheduledForFuture(record) && postsPermission?.edit && (
            <Tooltip title="Reschedule">
              <Button
                className="table-primary-fill-btn"
                style={{ background: "#185250", borderColor: "#185250" }}
                onClick={() => {
                  const scheduledAt = getScheduledMoment(record);
                  setReschedulePost(record);
                  // DatePicker/TimePicker in the Reschedule modal are the
                  // default antd (moment-based) components — passing a
                  // dayjs value throws "clone.weekday is not a function".
                  setRescheduleDate(scheduledAt?.isValid() ? moment(scheduledAt.toDate()) : null);
                  setRescheduleTime(scheduledAt?.isValid() ? moment(scheduledAt.toDate()) : null);
                  setRescheduleModalVisible(true);
                }}
              >
                <CalendarOutlined style={{ color: "#fff" }} />
              </Button>
            </Tooltip>
          )}
          {postsPermission?.delete && (
            <Tooltip title="Delete">
              <Button
                className="table-danger-fill-btn"
                onClick={() => {
                    setDeleteModalVisible(true);
                    setSelected(record);
                }}
              >
                <img src={DeleteTableBtn} alt="Delete" />
              </Button>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  const handleReschedule = () => {
    if (!rescheduleDate) {
      ShowToast("Please select a new date", "warning");
      return;
    }
    const newDate = rescheduleDate.format("YYYY-MM-DD");
    const newTime = rescheduleTime ? rescheduleTime.format("HH:mm") : null;
    setRescheduleLoading(true);
    request({
      url: `/admin/access-module/calender/move-date/${reschedulePost._id}`,
      method: "PUT",
      data: { selectedDate: newDate, ...(newTime ? { selectedTime: newTime } : {}) },
      onSuccess: () => {
        ShowToast("Post rescheduled successfully", "success");
        setRescheduleModalVisible(false);
        setReschedulePost(null);
        setRescheduleDate(null);
        setRescheduleTime(null);
        fetchData();
        setRescheduleLoading(false);
      },
      onError: (err) => {
        ShowToast(err?.response?.data?.message || "Failed to reschedule post", "error");
        setRescheduleLoading(false);
      },
    });
  };

  const handleMediaChange = (files) => {
    const formattedFiles = files?.map((file) => ({
      url: file.url,
      name: file.name,
      status: file.status,
    }));
    if (selectedPostType === "Carousel") {
      setAttachment(formattedFiles ? formattedFiles.slice(0, 1) : []);
    } else {
      setAttachment(formattedFiles || []);
    }
  };

  useEffect(() => {
    fetchData();
    contentThemeData();
  }, [searchText, selectedDate, selectedStatus, currentPage, pageSize, dashboardView]);

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <h2 style={{ margin: 0 }}>Client Posts</h2>
        </Col>
        <Col>
        <Space size="large">
  {/* 🔍 Search Input */}
  <Input
    placeholder="Search Title"
    value={searchText}
    onChange={(e) => {
      setSearchText(e.target.value);
      setCurrentPage(1);
    }}
    onPressEnter={fetchData}
    style={{ width: 170 }}
  />

  {/* 📅 Date Picker */}
  <DatePicker
    placeholder="Select Date"
    value={selectedDate}
    onChange={(date) => {
      setSelectedDate(date);
      setCurrentPage(1);
    }}
    allowClear
  />

  {/* 📌 Status Filter */}
  <Select
    placeholder="Filter Status"
    allowClear
    style={{ width: 200 }}
    value={selectedStatus}
    onChange={(value) => {
      setSelectedStatus(value);
      setCurrentPage(1);
    }}
    options={[
      { label: "Draft", value: "Draft" },
      { label: "Draft By Client", value: "Draft By Client" },
      { label: "Draft By Brand", value: "Draft By Brand" },
      { label: "Draft By Admin", value: "Draft By Admin" },
      { label: "Sent For Review", value: "Sent For Review" },
      { label: "Under Review", value: "Under Review" },
      { label: "Reviewed", value: "Reviewed" },
      { label: "Approved By Client", value: "Approved By Client" },
      { label: "Approved By Brand", value: "Approved By Brand" },
      { label: "Publish By Client", value: "Publish By Client" },
      { label: "Publish By Brand", value: "Publish By Brand" },
      { label: "Publish By Admin", value: "Publish By Admin" },
      { label: "Live", value: "Live" },
    ]}
  />

  {/* 🔄 Reset Button */}
  <Button
    onClick={() => {
      setSearchText("");
      setSelectedDate(null);
      setSelectedStatus(undefined);
      setCurrentPage(1);
      fetchData(); 
    }}
  >
    Reset Filters
  </Button>
</Space>

        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={data}
        className="table-responsive"
        rowKey="_id"
        pagination={{
          current: currentPage,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (page, pageSize) => {
            setCurrentPage(page);
            setPageSize(pageSize);
          },
        }}
      />

      <Modal
        className="table_modal delete-modal"
        footer={false}
        title={false}
        width={450}
        open={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
      >
        <div className="delete-modal-body">
          <img src={DeleteModalBtn} alt="delete" />
          <h3>Are you sure?</h3>
          <p>Are you sure you want to delete this post?</p>
          {/* LinkedIn live-post warning — only shown when the post is
              published on LinkedIn. Matches the warning in the Calendar
              tab's delete modal (FullCalender.js). */}
          {selected?.postId && (
            <div className="delete-linkedin-warning">
              <span className="delete-linkedin-warning-icon">⚠️</span>
              <div className="delete-linkedin-warning-text">
                <strong>This post is live on LinkedIn.</strong>
                <span> Deleting it here will also permanently remove it from your LinkedIn profile.</span>
              </div>
            </div>
          )}
          <div className="action-footer-buttons" style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 8 }}>
            <Button className="cancel-btn" onClick={() => setDeleteModalVisible(false)}>
              Cancel
            </Button>
            <Button className="delete-fill-btn delete-confirm-btn" onClick={deleteContent}>
              {selected?.postId ? "Yes, Delete from LinkedIn too" : "Yes, confirm"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Reschedule Modal */}
      <Modal
        // className="table_modal reschedule-modal"
        title="Reschedule Post"
        open={rescheduleModalVisible}
        onCancel={() => {
          setRescheduleModalVisible(false);
          setReschedulePost(null);
          setRescheduleDate(null);
          setRescheduleTime(null);
        }}
        footer={null}
        width={420}
      >
        <div style={{ padding: "16px 0" }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", marginBottom: 6, fontWeight: 500 }}>New Date</label>
            <DatePicker
              value={rescheduleDate}
              onChange={(date) => setRescheduleDate(date)}
              style={{ width: "100%" }}
              disabledDate={(current) => current && current < moment().startOf("day")}
            />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: "block", marginBottom: 6, fontWeight: 500 }}>New Time (optional)</label>
            <TimePicker
              value={rescheduleTime}
              onChange={(time) => setRescheduleTime(time)}
              format="HH:mm"
              style={{ width: "100%" }}
            />
          </div>
          <Button
            className="submitted-btn"
            icon={<CalendarOutlined />}
            loading={rescheduleLoading}
            onClick={handleReschedule}
            style={{ width: "100%" }}
          >
            Reschedule
          </Button>
        </div>
      </Modal>

      {/* View Post Modal */}
      <Modal
        width={757}
        className="table_modal"
        title="Post Details"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={null}
      >
        <div className="piller-view-detail">
          <div className="pill-detail-list">
            <h6>Title:</h6>
            <span>{selected?.title || "N/A"}</span>
          </div>
          <div className="pill-detail-list">
            <h6>Post Text:</h6>
            <NormalEditor 
              content={selected?.postText || "N/A"}
              onChange={(value) => setPostText(value)}
              disabled={true}
            />
          </div>
          <div className="pill-detail-list">
            <h6>Status:</h6>
            <span>{selected?.brandStatus || "N/A"}</span>
          </div>
          <div className="pill-detail-list">
            <h6>Last Updated:</h6>
            <span>
              {selected?.updated_at
                ? moment(selected.updated_at).format("YYYY-MM-DD HH:mm:ss")
                : "N/A"}
            </span>
          </div>
          <div className="pill-detail-list">
            <h6>Media:</h6>
            {selected?.media?.length > 0 ? (
              selected.media.map((url, idx) =>
                url ? (
                  <img
                    key={idx}
                    src={url}
                    alt={`Media ${idx + 1}`}
                    style={{
                      width: "60px",
                      height: "60px",
                      objectFit: "cover",
                      marginRight: "5px",
                      borderRadius: "5px",
                    }}
                  />
                ) : null
              )
            ) : (
              <span>N/A</span>
            )}
          </div>
        </div>
      </Modal>

      <Drawer
        className="post-upload-drawer"
        placement="left"
        title={false}
        width="90%"
        onClose={() => {
          resetForm();
          onClose();
          setIsEditMode(false);
          setIsDirty(false);
        }}
        open={open}
      >
        <div className={`create-post-container ${isDisable ? "b" : ""}`}>
          <div className="post-up-header">
            <h6>Create Post</h6>
          </div>
          <div className="drawer--up-body">
            <Row gutter={[32, 20]}>
              <Col span={24} lg={12}>
                <div className="post-section-main linkedin-preview-shell">
                  <div className="profile-header">
                    <div className="profile-post-images"></div>
                    <div className="profile-info">
                      <div className="profile-followers">3,123 followers</div>
                      <div className="profile-date">
                        {selectedDate} <CalendarOutlined />
                      </div>
                    </div>
                  </div>
                  <div className="upload-post-description">
                    {selectedPostType === "Media" && attachment && attachment.length > 0 && (
                      /\.(mp4|mov|avi|webm|mkv)(\?|$)/i.test(attachment[0]?.url || "") ? (
                        <video controls className="post-video" style={{ width: "100%", height: "auto" }}>
                          <source src={attachment[0].url} type="video/mp4" />
                        </video>
                      ) : (
                        <Carousel>
                          {attachment.map((file, index) => (
                            <img key={index} src={file.url} alt={`post preview ${index}`} className="post-image" />
                          ))}
                        </Carousel>
                      )
                    )}
                    {selectedPostType === "Poll" && pollQuestion && (
                      <div style={{ background: "#f5f5f5", borderRadius: 8, padding: 12, marginTop: 8 }}>
                        <div style={{ fontWeight: 600, marginBottom: 8 }}>📊 {pollQuestion}</div>
                        {pollResultsLoading ? (
                          <div style={{ textAlign: "center", padding: "12px 0", color: "#888", fontSize: 13 }}>Loading poll results…</div>
                        ) : pollResultsForbidden ? (
                          <div style={{ textAlign: "center", padding: "10px 0", color: "#faad14", fontSize: 12 }}>
                            ⚠️ Live results unavailable — LinkedIn read permissions not granted
                          </div>
                        ) : pollResults && pollResults.options && pollResults.options.length > 0 ? (
                          <>
                            {pollResults.options.map((opt, idx) => {
                              const total = pollResults.totalVoteCount || 0;
                              const pct = total > 0 ? Math.round((opt.voteCount / total) * 100) : 0;
                              return (
                                <div key={idx} style={{ marginBottom: 8 }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 3 }}>
                                    <span>{opt.text}</span>
                                    <span style={{ fontWeight: 600, color: "#0a66c2" }}>{pct}%</span>
                                  </div>
                                  <div style={{ background: "#e0e0e0", borderRadius: 4, height: 8, overflow: "hidden" }}>
                                    <div style={{ width: `${pct}%`, background: "#0a66c2", height: "100%", borderRadius: 4, transition: "width 0.4s" }} />
                                  </div>
                                  <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{opt.voteCount} vote{opt.voteCount !== 1 ? "s" : ""}</div>
                                </div>
                              );
                            })}
                            <div style={{ fontSize: 12, color: "#888", marginTop: 6, borderTop: "1px solid #e0e0e0", paddingTop: 6 }}>
                              {pollResults.totalVoteCount} total vote{pollResults.totalVoteCount !== 1 ? "s" : ""}
                              {pollResults.votingStatus === "CLOSED" && <span style={{ marginLeft: 8, color: "#f5222d" }}>● Closed</span>}
                              {pollResults.votingStatus === "OPEN" && <span style={{ marginLeft: 8, color: "#52c41a" }}>● Active</span>}
                            </div>
                          </>
                        ) : (
                          pollOptions.filter(Boolean).map((opt, idx) => (
                            <div key={idx} style={{ background: "#fff", border: "1px solid #d9d9d9", borderRadius: 4, padding: "6px 10px", marginBottom: 4, fontSize: 13 }}>
                              {opt}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                    {selectedPostType === "Carousel" && attachment && attachment.length > 0 && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 10, background: "#fff7e6", borderRadius: 6 }}>
                        <span style={{ fontSize: 24 }}>📄</span>
                        <span style={{ fontSize: 13 }}>{attachment[0]?.name || "PDF Document"}</span>
                      </div>
                    )}
                  </div>

                  {isLinkedInPosted && (
                    <div>
                      <div className="post-interactions">
                        <div className="like-counts">
                          <span>100</span>
                        </div>
                        <div className="cm-reort-text">
                          <span>7 Comment</span>
                          <span>3 Repost</span>
                        </div>
                      </div>
                      <div className="action-buttons">
                        <Button className="post-action-btn active">Like</Button>
                        <Button className="post-action-btn">Comment</Button>
                        <Button className="post-action-btn">Repost</Button>
                        <Button className="post-action-btn">Send</Button>
                      </div>
                    </div>
                  )}
                  <div className="post-footer-bottam">
                    {postText && postText.length < 10000 && (
                      <div className="post-footer-bottam-text-areaa">
                        {postText}
                      </div>
                    )}
                  </div>
                </div>
              </Col>

              <Col span={24} lg={12}>
                <Form
                  form={form}
                  layout="vertical"
                  initialValues={{
                    ...(isEditMode ? record : {}),
                    mediaType: "Media",
                    pollDuration: "3",
                  }}
                  onFinish={onFinish}
                  className="post-contant-outerpanel"
                  onValuesChange={(changedValues) => {
                    setIsDirty(true);
                    if (changedValues.postText !== undefined) {
                      setPostText(changedValues.postText);
                      setPostAnalysis(null);
                    }
                    // mediaType changes handled by handlePostTypeChange (Select onChange)
                  }}
                >
                  <div
                    className="post-editor-title"
                  >
                    <span className="post-editor-icon-box">
                      <FileTextOutlined />
                    </span>
                    <span className="post-editor-title-text">Post Content</span>
                    {isEditMode && record?.publishedExternally && (
                      <div style={{ marginLeft: 12, display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <Tag color="cyan">Published on LinkedIn (external)</Tag>
                        {record?.linkedinPostUrl && (
                          <a href={record.linkedinPostUrl} target="_blank" rel="noreferrer">
                            View on LinkedIn
                          </a>
                        )}
                      </div>
                    )}
                    {isEditMode && (
                      <div className="post-detail-page">
                        <Button
                          className="fill-btn"
                          onClick={() => setIsDisable(false)}
                        >
                          Edit Post
                        </Button>
                      </div>
                    )}
                  </div>

                  <Form.Item
                    name="title"
                    label="Title"
                    rules={[
                      { required: true, message: "Please enter a title" },
                    ]}
                  >
                    <Input
                      placeholder="Enter post title"
                      className="post-input"
                      disabled={isDisable || !postsPermission?.edit || isPublishedLocked}
                    />
                  </Form.Item>

                  <Form.Item
                    name="contentTheme"
                    label="Content Theme"
                    rules={[
                      {
                        required: true,
                        message: "Please select content theme",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select content theme"
                      onChange={handleThemeChange}
                      disabled={isDisable || !postsPermission?.edit || isPublishedLocked}
                    >
                      {themeData?.map((theme) => (
                        <Select.Option key={theme._id} value={theme._id}>
                          {theme.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="subPillar"
                    label="Sub Pillar"
                    rules={[
                      { required: true, message: "Please select a pillar" },
                    ]}
                  >
                    <Select placeholder="Select pillar" disabled={isDisable || !postsPermission?.edit || isPublishedLocked}>
                      {subPillars?.flat()?.map((pillar, index) => (
                        <Select.Option key={index} value={pillar}>
                          {pillar}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="mediaType"
                    label="Type"
                    rules={[
                      { required: true, message: "Please select post type" },
                    ]}
                  >
                    <Select
                      onChange={handlePostTypeChange}
                      disabled={isDisable || !postsPermission?.edit || isPublishedLocked}
                    >
                      <Select.Option value="Media">🖼️ Media (Image / Video)</Select.Option>
                      <Select.Option value="Poll">📊 Poll</Select.Option>
                      <Select.Option value="Carousel">📄 Carousel (PDF)</Select.Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="postText"
                    label="Post text"
                    rules={[
                      { required: true, message: "Please enter post text" },
                    ]}
                  >
                    <TextArea
                      placeholder="Write caption, #hashtag, @mentions, link or add emoji..."
                      rows={5}
                      maxLength={1000}
                      className="post-input"
                      disabled={!postsPermission?.edit}
                    />
                  </Form.Item>

                  {/* Content Intelligence — readability, suggestions, fact check */}
                  <div className="post-ci-panel">
                    <div className="post-ci-header">
                      <div>
                        <h4 className="post-ci-title">Content Intelligence</h4>
                        <p className="post-ci-subtitle">
                          {postAnalysis
                            ? "Analysis complete — review your scores below"
                            : "Analyze your content for readability, suggestions and facts"}
                        </p>
                      </div>
                      <Button
                        type="primary"
                        icon={<ThunderboltOutlined />}
                        loading={postAnalysisLoading}
                        onClick={handleAnalyzePostContent}
                        disabled={!postsPermission?.edit}
                        className="post-ci-analyze-btn"
                      >
                        Analyze Content
                      </Button>
                    </div>

                    <div className="post-ci-cards">
                      {/* Readability — overall score is 0-100 (average of Flesch Reading
                          Ease and Dale-Chall Grade, each graded per-point across its own
                          target band); same engine and scale as the Writing Assistant card. */}
                      {(() => {
                        const score = postAnalysis?.readability_details?.overall_score;
                        const readColor = score >= 80 ? "#16a34a" : score >= 50 ? "#d97706" : "#dc2626";
                        const status = score >= 80 ? "Excellent" : score >= 50 ? "Needs Improvement" : "Poor";
                        return (
                          <div className="post-ci-card">
                            <div className="post-ci-card-top">
                              <div className="post-ci-icon" style={{ background: score != null ? `${readColor}1a` : "#f1f5f9" }}>
                                <FileTextOutlined style={{ color: score != null ? readColor : "#94a3b8" }} />
                              </div>
                              <span className="post-ci-label">Readability Score</span>
                            </div>
                            {score != null ? (
                              <div style={{ textAlign: "center", margin: "2px 0 8px" }}>
                                <span style={{ fontSize: 30, fontWeight: 800, color: readColor }}>{score}</span>
                                <span style={{ fontSize: 14, color: "#94a3b8" }}>/100</span>
                                <div style={{ height: 6, background: "#f1f5f9", borderRadius: 3, overflow: "hidden", margin: "6px auto 0" }}>
                                  <div style={{ width: `${score}%`, height: "100%", background: readColor, borderRadius: 3 }} />
                                </div>
                                <span className="post-ci-badge" style={{ background: `${readColor}18`, color: readColor, marginTop: 6, display: "inline-block" }}>
                                  {status}
                                </span>
                              </div>
                            ) : (
                              <div className="post-ci-value" style={{ color: "#cbd5e1" }}>—</div>
                            )}
                            <p className="post-ci-note">
                              Average of Flesch Reading Ease and Dale-Chall Grade, scored against their target ranges.
                            </p>
                          </div>
                        );
                      })()}

                      {/* Guideline Checker — our own LLM-based feature. originality.ai's
                          version isn't reachable via their API at all (confirmed against
                          their published /scan parameter list). */}
                      <div className="post-ci-card">
                        <div className="post-ci-card-top">
                          <div className="post-ci-icon" style={{ background: "#eff6ff" }}>
                            <CheckOutlined style={{ color: "#2563eb" }} />
                          </div>
                          <span className="post-ci-label">Guideline Checker</span>
                        </div>
                        {postGuidelineCheckLoading && (
                          <p className="post-ci-empty">Checking guidelines...</p>
                        )}
                        {!postGuidelineCheckLoading && postGuidelineCheck?.alignment_score != null && (
                          <>
                            <div
                              className="post-ci-accuracy"
                              style={{
                                background: postGuidelineCheck.alignment_score >= 80 ? "#dcfce7" : postGuidelineCheck.alignment_score >= 40 ? "#fef3c7" : "#fee2e2",
                                color: postGuidelineCheck.alignment_score >= 80 ? "#15803d" : postGuidelineCheck.alignment_score >= 40 ? "#b45309" : "#b91c1c",
                              }}
                            >
                              {postGuidelineCheck.alignment_score >= 80 ? "✓" : postGuidelineCheck.alignment_score >= 40 ? "⚠" : "✗"}{" "}
                              {postGuidelineCheck.alignment_score}% - {postGuidelineCheck.alignment_score >= 90 ? "Highly Aligned" : postGuidelineCheck.alignment_score >= 70 ? "Mostly Aligned" : postGuidelineCheck.alignment_score >= 40 ? "Needs Revision" : "Poorly Aligned"}
                            </div>
                            {postGuidelineCheck.summary && (
                              <p className="post-ci-note" style={{ marginTop: 6 }}>{postGuidelineCheck.summary}</p>
                            )}
                            {(postGuidelineCheck.sentences || []).length ? (
                              <ul className="post-ci-fact-list">
                                {postGuidelineCheck.sentences.slice(0, 5).map((s, i) => (
                                  <li key={i} className="post-ci-fact-item">
                                    <div className="post-ci-fact-claim">{s.text}</div>
                                    {s.explanation && <p className="post-ci-note" style={{ margin: "2px 0" }}>{s.explanation}</p>}
                                    {s.suggestion && (
                                      <span className="post-ci-fact-badge" style={{ background: "#dcfce718", color: "#15803d" }}>
                                        {s.suggestion}{" "}
                                        <button
                                          type="button"
                                          onClick={() => handleCopyPostGuidelineSuggestion(s.suggestion)}
                                          style={{ marginLeft: 6, border: "none", background: "transparent", color: "#2563eb", cursor: "pointer", textDecoration: "underline" }}
                                        >
                                          Copy
                                        </button>
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="post-ci-empty">No guideline violations found.</p>
                            )}
                          </>
                        )}
                        {!postGuidelineCheckLoading && postGuidelineCheck?.alignment_score == null && (
                          <p className="post-ci-empty">
                            {postGuidelineCheck ? "Guideline check unavailable." : "Run analysis to check style guideline alignment."}
                          </p>
                        )}
                      </div>

                      {/* Fact Check */}
                      <div className="post-ci-card">
                        <div className="post-ci-card-top">
                          <div className="post-ci-icon" style={{ background: "#fef9c3" }}>
                            <SearchOutlined style={{ color: "#ca8a04" }} />
                          </div>
                          <span className="post-ci-label">Fact Check</span>
                        </div>
                        {postAnalysis?.accuracy_score != null && (
                          <div
                            className="post-ci-accuracy"
                            style={{
                              background: postAnalysis.accuracy_score >= 70 ? "#dcfce7" : postAnalysis.accuracy_score >= 40 ? "#fef3c7" : "#fee2e2",
                              color: postAnalysis.accuracy_score >= 70 ? "#15803d" : postAnalysis.accuracy_score >= 40 ? "#b45309" : "#b91c1c",
                            }}
                          >
                            {postAnalysis.accuracy_score >= 70 ? "✓" : postAnalysis.accuracy_score >= 40 ? "⚠" : "✗"}{" "}
                            Accuracy: <strong>{postAnalysis.accuracy_score}%</strong>
                          </div>
                        )}
                        {(postAnalysis?.facts || []).length ? (
                          <ul className="post-ci-fact-list">
                            {postAnalysis.facts.map((f, i) => {
                              const v = f.verdict || "Unverified";
                              const vColor = v === "Supported" ? "#15803d" : v === "Partially Supported" ? "#b45309" : v === "Contradicted" ? "#b91c1c" : "#64748b";
                              return (
                                <li key={i} className="post-ci-fact-item">
                                  <div className="post-ci-fact-claim">{f.claim}</div>
                                  <span className="post-ci-fact-badge" style={{ background: `${vColor}18`, color: vColor }}>
                                    {v} {f.confidence != null ? `· ${f.confidence}%` : ""}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <p className="post-ci-empty">
                            {postAnalysis
                              ? "No verifiable claims found in this content."
                              : "Run analysis to fact-check your claims."}
                          </p>
                        )}
                      </div>

                      {/* Plagiarism (originality.ai, falls back when unavailable) */}
                      <div className="post-ci-card">
                        <div className="post-ci-card-top">
                          <div className="post-ci-icon" style={{ background: "#ffe4e6" }}>
                            <CopyOutlined style={{ color: "#be123c" }} />
                          </div>
                          <span className="post-ci-label">Plagiarism</span>
                        </div>
                        {postAnalysis?.plagiarism?.plagiarism_score != null ? (
                          <>
                            <div
                              className="post-ci-accuracy"
                              style={{
                                background: postAnalysis.plagiarism.plagiarism_score <= 10 ? "#dcfce7" : postAnalysis.plagiarism.plagiarism_score <= 30 ? "#fef3c7" : "#fee2e2",
                                color: postAnalysis.plagiarism.plagiarism_score <= 10 ? "#15803d" : postAnalysis.plagiarism.plagiarism_score <= 30 ? "#b45309" : "#b91c1c",
                              }}
                            >
                              {postAnalysis.plagiarism.plagiarism_score <= 10 ? "✓" : postAnalysis.plagiarism.plagiarism_score <= 30 ? "⚠" : "✗"}{" "}
                              Matched: <strong>{postAnalysis.plagiarism.plagiarism_score}%</strong>
                            </div>
                            {(postAnalysis.plagiarism.matches || []).length ? (
                              <ul className="post-ci-fact-list">
                                {postAnalysis.plagiarism.matches.slice(0, 5).map((m, i) => (
                                  <li key={i} className="post-ci-fact-item">
                                    <div className="post-ci-fact-claim">
                                      <a href={m.url} target="_blank" rel="noreferrer">{m.url}</a>
                                    </div>
                                    <span className="post-ci-fact-badge" style={{ background: "#be123c18", color: "#be123c" }}>
                                      {m.similarity}% match
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="post-ci-empty">No matching sources found.</p>
                            )}
                          </>
                        ) : (
                          <p className="post-ci-empty">
                            {postAnalysis
                              ? "Plagiarism check unavailable — showing fallback analysis only."
                              : "Run analysis to scan for plagiarism."}
                          </p>
                        )}
                      </div>

                      {/* AI Detection (originality.ai, falls back when unavailable) */}
                      <div className="post-ci-card">
                        <div className="post-ci-card-top">
                          <div className="post-ci-icon" style={{ background: "#ede9fe" }}>
                            <RobotOutlined style={{ color: "#6d28d9" }} />
                          </div>
                          <span className="post-ci-label">AI Detection</span>
                        </div>
                        {postAnalysis?.ai_detection?.ai_score != null ? (
                          <div
                            className="post-ci-accuracy"
                            style={{
                              background: postAnalysis.ai_detection.ai_score < 20 ? "#dcfce7" : postAnalysis.ai_detection.ai_score < 50 ? "#fef3c7" : "#fee2e2",
                              color: postAnalysis.ai_detection.ai_score < 20 ? "#15803d" : postAnalysis.ai_detection.ai_score < 50 ? "#b45309" : "#b91c1c",
                            }}
                          >
                            {postAnalysis.ai_detection.ai_score < 20 ? "✓" : postAnalysis.ai_detection.ai_score < 50 ? "⚠" : "✗"}{" "}
                            AI-likely: <strong>{postAnalysis.ai_detection.ai_score}%</strong>
                          </div>
                        ) : (
                          <p className="post-ci-empty">
                            {postAnalysis
                              ? "AI detection unavailable — showing fallback analysis only."
                              : "Run analysis to check for AI-generated content."}
                          </p>
                        )}
                        {postAnalysis?.ai_detection?.verdict && (
                          <p className="post-ci-empty" style={{ marginTop: 4 }}>{postAnalysis.ai_detection.verdict}</p>
                        )}
                      </div>

                      {/* Grammar & Spelling (originality.ai, falls back when unavailable) */}
                      <div className="post-ci-card">
                        <div className="post-ci-card-top">
                          <div className="post-ci-icon" style={{ background: "#dcfce7" }}>
                            <CheckOutlined style={{ color: "#16a34a" }} />
                          </div>
                          <span className="post-ci-label">Grammar & Spelling</span>
                        </div>
                        {postAnalysis?.grammar?.score != null ? (() => {
                          const issues = postAnalysis.grammar.issues || [];
                          return (
                            <>
                              <div
                                className="post-ci-accuracy"
                                style={{
                                  background: postAnalysis.grammar.score >= 90 ? "#dcfce7" : postAnalysis.grammar.score >= 70 ? "#fef3c7" : "#fee2e2",
                                  color: postAnalysis.grammar.score >= 90 ? "#15803d" : postAnalysis.grammar.score >= 70 ? "#b45309" : "#b91c1c",
                                }}
                              >
                                {postAnalysis.grammar.score >= 90 ? "✓" : postAnalysis.grammar.score >= 70 ? "⚠" : "✗"}{" "}
                                Correctness: <strong>{postAnalysis.grammar.score}%</strong>
                                {postAnalysis.grammar.grade ? ` · Grade ${postAnalysis.grammar.grade}` : ""}
                              </div>
                              {issues.length ? (
                                <ul className="post-ci-fact-list">
                                  {issues.slice(0, 4).map((iss, i) => (
                                    <li key={i} className="post-ci-fact-item">
                                      <div className="post-ci-fact-claim">{iss.message}</div>
                                      <span className="post-ci-fact-badge" style={{ background: "#fef3c718", color: "#b45309" }}>
                                        {iss.category || "Issue"}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="post-ci-empty">No grammar or spelling issues found.</p>
                              )}
                            </>
                          );
                        })() : (
                          <p className="post-ci-empty">
                            {postAnalysis
                              ? "Grammar check unavailable — showing fallback analysis only."
                              : "Run analysis to check grammar and spelling."}
                          </p>
                        )}
                      </div>

                      {/* Content Quality (originality.ai's High/Medium/Low classifier) */}
                      <div className="post-ci-card">
                        <div className="post-ci-card-top">
                          <div className="post-ci-icon" style={{ background: "#e0f2fe" }}>
                            <FileTextOutlined style={{ color: "#0284c7" }} />
                          </div>
                          <span className="post-ci-label">Content Quality</span>
                        </div>
                        {postAnalysis?.content_quality?.label ? (
                          <>
                            <div
                              className="post-ci-accuracy"
                              style={{
                                background: postAnalysis.content_quality.label === "High" ? "#dcfce7" : postAnalysis.content_quality.label === "Medium" ? "#fef3c7" : "#fee2e2",
                                color: postAnalysis.content_quality.label === "High" ? "#15803d" : postAnalysis.content_quality.label === "Medium" ? "#b45309" : "#b91c1c",
                              }}
                            >
                              {postAnalysis.content_quality.label === "High" ? "✓" : postAnalysis.content_quality.label === "Medium" ? "⚠" : "✗"}{" "}
                              {postAnalysis.content_quality.label}
                              {postAnalysis.content_quality.confidence != null ? ` · ${postAnalysis.content_quality.confidence}%` : ""}
                            </div>
                            {Object.keys(postAnalysis.content_quality.probs || {}).length > 0 && (
                              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                                {["High", "Medium", "Low"].filter((band) => postAnalysis.content_quality.probs[band] != null).map((band) => (
                                  <div key={band} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#64748b" }}>
                                    <span>{band}</span><span>{postAnalysis.content_quality.probs[band]}%</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="post-ci-empty">
                            {postAnalysis
                              ? "Content quality check unavailable — showing fallback analysis only."
                              : "Run analysis to check content quality."}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <Form.Item name="addFirstComment" valuePropName="checked">
                    <div className="post-comment-toggle">
                      <p>Visibility</p>
                      <Form.Item
                        name="addFirstComment"
                        valuePropName="checked"
                        noStyle
                      >
                        <Switch
                          className="post-type-checkbox"
                          disabled={isDisable || !postsPermission?.edit || isPublishedLocked}
                        />
                      </Form.Item>
                    </div>
                  </Form.Item>

                  {selectedPostType === "Media" && postsPermission?.edit && (
                    <Form.Item
                      className="custom-media-item"
                      label="Media (Image / Video)"
                      name="media"
                    >
                      <MultipleImageUpload
                        fileType={["image/png","image/jpeg","image/jpg","image/gif","image/webp","video/mp4"]}
                        imageType="blog"
                        btnName="Attach Image or Video"
                        onChange={handleMediaChange}
                        data={attachment}
                        isDisable={isDisable || isPublishedLocked}
                      />
                    </Form.Item>
                  )}
                  {selectedPostType === "Poll" && postsPermission?.edit && (
                    <div className="poll-form-section" style={{ marginBottom: 16 }}>
                      <Form.Item
                        name="pollQuestion"
                        label="Poll Question"
                        rules={[{ required: true, message: "Please enter the poll question" }]}
                      >
                        <Input
                          placeholder="What would you like to ask?"
                          className="post-input"
                          disabled={isDisable || isPublishedLocked}
                          maxLength={140}
                          showCount
                          onChange={(e) => setPollQuestion(e.target.value)}
                        />
                      </Form.Item>
                      <Form.Item label="Poll Options (2–4)" required>
                        {pollOptions.map((opt, idx) => (
                          <div key={idx} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                            <Input
                              placeholder={`Option ${idx + 1}`}
                              value={opt}
                              disabled={isDisable || isPublishedLocked}
                              maxLength={30}
                              showCount
                              onChange={(e) => {
                                const updated = [...pollOptions];
                                updated[idx] = e.target.value;
                                setPollOptions(updated);
                              }}
                            />
                            {pollOptions.length > 2 && (
                              <Button danger size="small" disabled={isDisable || isPublishedLocked}
                                onClick={() => setPollOptions(pollOptions.filter((_, i) => i !== idx))}>
                                ✕
                              </Button>
                            )}
                          </div>
                        ))}
                        {pollOptions.length < 4 && !isDisable && !isPublishedLocked && (
                          <Button type="dashed" size="small"
                            onClick={() => setPollOptions([...pollOptions, ""])}>
                            + Add Option
                          </Button>
                        )}
                      </Form.Item>
                      <Form.Item name="pollDuration" label="Poll Duration" initialValue="3">
                        <Select disabled={isDisable || isPublishedLocked}>
                          <Select.Option value="1">1 Day</Select.Option>
                          <Select.Option value="3">3 Days</Select.Option>
                          <Select.Option value="7">1 Week</Select.Option>
                          <Select.Option value="14">2 Weeks</Select.Option>
                        </Select>
                      </Form.Item>
                    </div>
                  )}
                  {selectedPostType === "Carousel" && (
                    <>
                      <Form.Item
                        name="carouselDocumentTitle"
                        label="Document title"
                        rules={[{ required: true, message: "Please add a descriptive title to your document" }]}
                        extra={<span style={{ fontSize: 12, color: "#888" }}>Add a descriptive title to your document</span>}
                      >
                        <Input
                          placeholder="Add a descriptive title to your document"
                          disabled={isDisable || isPublishedLocked}
                          maxLength={200}
                          showCount
                        />
                      </Form.Item>
                      {postsPermission?.edit && (
                        <Form.Item
                          className="custom-media-item"
                          label="PDF Document (Carousel)"
                          name="media"
                          rules={[{ required: true, message: "Please upload a PDF document" }]}
                        >
                          <MultipleImageUpload
                            fileType={["application/pdf"]}
                            imageType="blog"
                            btnName="Upload PDF"
                            maxCount={1}
                            onChange={handleMediaChange}
                            data={attachment}
                            isDisable={isDisable || isPublishedLocked}
                          />
                        </Form.Item>
                      )}
                    </>
                  )}

                  {isEditMode && record?.action === "review" && (
                    <>
                      {record.commentData && (
                        <div style={{ marginBottom: 12 }}>
                          <label
                            style={{
                              fontWeight: "bold",
                              fontSize: 16,
                              display: "block",
                              marginBottom: 6,
                            }}
                          >
                            Comments
                          </label>

                          <div
                            style={{
                              maxHeight: "300px",
                              overflowY: "auto",
                              background: "#fafafa",
                              border: "1px solid #ddd",
                              borderRadius: "8px",
                              padding: "10px",
                            }}
                          >
                            {record?.commentData?.length > 0 ? (
                              record?.commentData
                                .slice()
                                .reverse()
                                ?.map((data, index) => {
                                  const isBrand = data.type === "brand";

                                  return (
                                    <div
                                      key={index}
                                      style={{
                                        display: "flex",
                                        justifyContent: isBrand
                                          ? "flex-end"
                                          : "flex-start",
                                        marginBottom: "10px",
                                      }}
                                    >
                                      <div style={getCommentStyle(data.type)}>
                                        <div
                                          style={{
                                            fontSize: 14,
                                            marginBottom: 4,
                                          }}
                                        >
                                          {data.comment}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 12,
                                            color: "#888",
                                          }}
                                        >
                                          <strong>By:</strong>{" "}
                                          {data.commentUserId?.name} (
                                          {data.type} ) |{" "}
                                          {dayjs(data.time).format(
                                            "DD MMM YYYY, HH:mm"
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })
                            ) : (
                              <p
                                style={{
                                  color: "#999",
                                  textAlign: "center",
                                  margin: 0,
                                }}
                              >
                                No comments yet
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {postsPermission?.edit && (
                        <Form.Item name="commentbox" label="Comment box">
                          <TextArea
                            placeholder="please enter your comment..."
                            rows={5}
                            className="post-input"
                          />
                        </Form.Item>
                      )}
                    </>
                  )}

                  <div className="action-footer-buttons">
                    {!isScheduledLocked && postsPermission?.edit && !(isEditMode && record?.action === "approved") && (
                      <Button
                        htmlType="submit"
                        className="submitted-btn"
                        icon={<CheckCircleOutlined />}
                        onClick={() => setActionType("review")}
                      >
                        Send for review
                      </Button>
                    )}

                    {!isScheduledLocked && (isEditMode ? (
                      record?.action !== "approved" ? (
                        postsPermission?.edit && (
                          <Button
                            htmlType="submit"
                            className="schedule-btn"
                            icon={<ClockCircleOutlined />}
                            onClick={() => {
                              setActionType("draft");
                            }}
                          >
                            Save as Draft
                          </Button>
                        )
                      ) : (
                        postsPermission?.edit && (
                          <Button
                            className="schedule-btn"
                            icon={<ClockCircleOutlined />}
                            onClick={() => {
                              setActionType("publish");
                              setIsModalOpen(true);
                            }}
                          >
                            Publish
                          </Button>
                        )
                      )
                    ) : (
                      postsPermission?.add && (
                        <Button
                          htmlType="submit"
                          className="schedule-btn"
                          icon={<ClockCircleOutlined />}
                          onClick={() => {
                            setActionType("draft");
                          }}
                          disabled={isDisable}
                        >
                          Save as Draft
                        </Button>
                      )
                    ))}

                    {isEditMode && record && isPostScheduledForFuture(record) && postsPermission?.edit && (
                      <Button
                        className="submitted-btn"
                        icon={<CalendarOutlined />}
                        onClick={() => {
                          const scheduledAt = getScheduledMoment(record);
                          setReschedulePost(record);
                          // DatePicker/TimePicker in the Reschedule modal are the
                          // default antd (moment-based) components — passing a
                          // dayjs value throws "clone.weekday is not a function".
                          setRescheduleDate(scheduledAt?.isValid() ? moment(scheduledAt.toDate()) : null);
                          setRescheduleTime(scheduledAt?.isValid() ? moment(scheduledAt.toDate()) : null);
                          setRescheduleModalVisible(true);
                        }}
                      >
                        Reschedule
                      </Button>
                    )}

                    {isEditMode && postsPermission?.edit && !record?.publishedExternally && (
                      <Button
                        className="submitted-btn"
                        onClick={() => {
                          setLinkedinUrlInput("");
                          setIsMarkExternalModalOpen(true);
                        }}
                      >
                        Mark Published on LinkedIn
                      </Button>
                    )}
                  </div>
                </Form>
              </Col>
            </Row>
          </div>
        </div>
      </Drawer>

      <Modal
        title="Add Comment"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
      >
        <Input.TextArea
          rows={4}
          placeholder="Enter your comment..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        <div style={{ marginTop: 16, textAlign: "right" }}>
          {postsPermission?.edit && (
            <Button
              type="primary"
              style={{ marginRight: 8 }}
              onClick={() => handleAction("Review")}
            >
              Review
            </Button>
          )}
          {postsPermission?.edit && (
            <Button danger onClick={() => handleAction("Reject")}>
              Reject
            </Button>
          )}
        </div>
      </Modal>

      <Modal
        title="Mark Published on LinkedIn"
        open={isMarkExternalModalOpen}
        onCancel={() => {
          setIsMarkExternalModalOpen(false);
          setLinkedinUrlInput("");
        }}
        footer={null}
      >
        <p>
          Paste the URL of the post you already published on LinkedIn. Its
          engagement metrics will start syncing automatically.
        </p>
        <Input
          placeholder="https://www.linkedin.com/feed/update/..."
          value={linkedinUrlInput}
          onChange={(e) => setLinkedinUrlInput(e.target.value)}
        />
        <div style={{ marginTop: 16, textAlign: "right" }}>
          <Button
            style={{ marginRight: 8 }}
            onClick={() => {
              setIsMarkExternalModalOpen(false);
              setLinkedinUrlInput("");
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            loading={markExternalLoading}
            onClick={handleMarkPublishedExternally}
          >
            Confirm
          </Button>
        </div>
      </Modal>

      {/* Post-type change confirmation */}
      <Modal
        open={typeChangeModalVisible}
        onCancel={() => { setTypeChangeModalVisible(false); setPendingPostType(null); }}
        title="Change Post Type?"
        centered
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => { setTypeChangeModalVisible(false); setPendingPostType(null); }}>
              Cancel
            </Button>
            <Button
              type="primary"
              danger
              onClick={() => {
                applyPostTypeChange(pendingPostType);
                setTypeChangeModalVisible(false);
                setPendingPostType(null);
              }}
            >
              Change Type
            </Button>
          </div>
        }
      >
        <p style={{ margin: "8px 0 4px" }}>
          Are you sure you want to change the post type? Changing the post type will remove the data
          associated with the current type. This action cannot be undone.
        </p>
      </Modal>
    </>
  );
};

export default ClientPostTabs;
